import { crnFor } from '../../domain/raise/raiseCalc';
import type { Application, BankLine, Fund, Offer, UnitClass } from '../types';
import { FIDES_TENANT_ID } from './seedReference';
import { seedApplicationLinks } from './seedEntities';

export const RAISE_FUND: Fund = {
  id: 'f-mez3',
  tenantId: FIDES_TENANT_ID,
  name: 'Fides Mezzanine Trust 3 (Elmstead)',
  code: 'MEZ3',
  strategy: 'Private Credit',
  status: 'RAISING',
  basis: 'FIXED_UNITS',
  trustee: 'Demo Trustee Services Pty Ltd',
  abn: '00 888 888 883',
  inceptionDate: '2026-10-31',
  maturityDate: '2028-04-30',
  distributionFrequency: 'QUARTERLY',
  targetSize: 3_000_000,
  bank: {
    bsb: '083-004',
    accountNumber: '000551209',
    accountName: 'MEZ3 Applications Account',
    verified: true,
  },
  apcaUserId: '301507',
  bpayBillerCode: '384528',
  registryLiveDate: '2026-09-28',
  description:
    'Single-project mezzanine loan on a fictional townhouse development. Raising now; units are issued together at financial close.',
};

export const RAISE_CLASS: UnitClass = {
  id: 'c-mez3-o',
  fundId: 'f-mez3',
  name: 'Ordinary Units',
  code: 'ORD',
  issuePrice: 1,
  unitPrice: 1,
  targetReturnPct: 12,
  managementFeePct: 1.5,
  minInvestment: 100_000,
  open: true,
  priceHistory: [{ date: '2026-09-28', price: 1 }],
};

export const RAISE_OFFER: Offer = {
  id: 'of-mez3',
  tenantId: FIDES_TENANT_ID,
  fundId: 'f-mez3',
  classId: 'c-mez3-o',
  title: 'Fides Mezzanine Trust 3 (Elmstead) – Ordinary Units',
  status: 'PUBLISHED',
  summary: RAISE_FUND.description,
  url: 'invest.fidescapital.com.au/mez3/offer',
  closesAt: '2026-10-24T17:00',
  showCountdown: true,
  showInInvestorCentre: true,
  publicApply: false,
  issueAtClose: true,
  minInvestment: 100_000,
  increment: 10_000,
  paymentMethods: ['EFT', 'BPAY'],
  documents: ['Information Memorandum.pdf', 'Trust Deed.pdf'],
  brandColour: '#143c34',
  publishedAt: '2026-09-28T10:00:00',
  publishedBy: 'Jason Lont',
};

interface RaiseSpec {
  name: string;
  type: Application['applicantType'];
  contact: string;
  amount: number;
  paid: number;
  status?: Application['status'];
  holderId?: string;
  loginId?: string;
  submitted: string;
}

const SPECS: RaiseSpec[] = [
  {
    name: 'Bennett Super Pty Ltd ATF Bennett Superannuation Fund',
    type: 'SMSF',
    contact: 'Olivia Bennett',
    amount: 200_000,
    paid: 200_000,
    holderId: 'h-bennett-smsf',
    loginId: 'il-bennett',
    submitted: '2026-09-30',
  },
  {
    name: 'Mehta Ventures Pty Ltd',
    type: 'COMPANY',
    contact: 'Raj Mehta',
    amount: 500_000,
    paid: 500_000,
    holderId: 'h-mehta-co',
    loginId: 'il-mehta',
    submitted: '2026-10-01',
  },
  {
    name: 'Calloway Family Pty Ltd ATF Calloway Trust',
    type: 'TRUST',
    contact: 'Edith Calloway',
    amount: 250_000,
    paid: 250_000,
    submitted: '2026-10-02',
  },
  {
    name: 'Anika & Dev Raman',
    type: 'INDIVIDUAL',
    contact: 'Anika Raman',
    amount: 100_000,
    paid: 100_000,
    submitted: '2026-10-03',
  },
  {
    name: 'Osterley Super Pty Ltd ATF Osterley SMSF',
    type: 'SMSF',
    contact: 'Gareth Osterley',
    amount: 150_000,
    paid: 100_000,
    submitted: '2026-10-05',
  },
  {
    name: 'Brightwater Holdings Pty Ltd',
    type: 'COMPANY',
    contact: 'Leah Brightwater',
    amount: 300_000,
    paid: 300_000,
    submitted: '2026-10-06',
  },
  {
    name: 'Juno Park Investments Pty Ltd',
    type: 'COMPANY',
    contact: 'Marcus Juno',
    amount: 400_000,
    paid: 0,
    submitted: '2026-10-09',
  },
  {
    name: 'Tamsin Hale',
    type: 'INDIVIDUAL',
    contact: 'Tamsin Hale',
    amount: 100_000,
    paid: 100_000,
    status: 'KYC_REVIEW',
    submitted: '2026-10-10',
  },
  {
    name: 'Ferris Lane Pty Ltd',
    type: 'COMPANY',
    contact: 'Owen Ferris',
    amount: 250_000,
    paid: 0,
    status: 'IN_PROGRESS',
    submitted: '',
  },
  {
    name: 'Marlow & Finch Partners',
    type: 'PARTNERSHIP',
    contact: 'Cora Marlow',
    amount: 150_000,
    paid: 0,
    submitted: '2026-10-12',
  },
];

const DONE = 'DONE' as const;

export function buildRaiseApplications(): Application[] {
  return SPECS.map((spec, index) => {
    const id = `app-mez3-${index + 1}`;
    const status = spec.status ?? 'SUBMITTED';
    const submitted = status !== 'IN_PROGRESS';
    const reference = `APP-26-${1200 + index}`;
    const crn = crnFor(id);
    return {
      id,
      reference,
      fundId: 'f-mez3',
      classId: 'c-mez3-o',
      ...seedApplicationLinks(id, spec.holderId),
      applicantName: spec.name,
      applicantType: spec.type,
      contactName: spec.contact,
      email:
        `${spec.contact.split(' ')[0]}.${spec.contact.split(' ').slice(-1)[0]}@example.com`.toLowerCase(),
      amount: spec.amount,
      status,
      createdAt: spec.submitted || '2026-10-11',
      submittedAt: submitted ? spec.submitted : undefined,
      fundsReceivedAt: spec.paid >= spec.amount ? spec.submitted : undefined,
      checklist: {
        entity: DONE,
        owners: DONE,
        identity: status === 'KYC_REVIEW' ? 'ISSUE' : submitted ? DONE : 'PENDING',
        tax: DONE,
        bank: DONE,
        wholesale: submitted ? DONE : 'PENDING',
        signed: submitted ? DONE : 'PENDING',
        funds: spec.paid >= spec.amount ? DONE : 'PENDING',
      },
      notes: status === 'KYC_REVIEW' ? ['Identity check referred: name mismatch on licence'] : [],
      investorLoginId: spec.loginId,
      payableNow: spec.amount,
      paymentInstruction: submitted
        ? {
            crn,
            fileName: `MEZ3_application_${reference}_payment_instruction.pdf`,
            generatedAt: `${spec.submitted}T10:00:00`,
            sentAt: `${spec.submitted}T10:00:00`,
            reminders: spec.paid < spec.amount ? ['2026-10-14T09:00:00'] : [],
          }
        : undefined,
      receipts: spec.paid
        ? [
            {
              id: `rc-mez3-${index + 1}`,
              date: spec.submitted,
              amount: spec.paid,
              reference: crn,
              source: 'BANK_FEED' as const,
              recordedBy: 'Bank feed',
            },
          ]
        : [],
    };
  });
}

export const RAISE_BANK_LINES: BankLine[] = [
  {
    id: 'bl-r1',
    fundId: 'f-mez3',
    date: '2026-10-15',
    amount: 150_000,
    description: 'EFT MARLOW FINCH',
    reference: crnFor('app-mez3-10'),
    status: 'SUGGESTED',
    suggestedApplicationId: 'app-mez3-10',
  },
  {
    id: 'bl-r2',
    fundId: 'f-mez3',
    date: '2026-10-16',
    amount: 50_000,
    description: 'BPAY OSTERLEY SUPER',
    reference: crnFor('app-mez3-5'),
    status: 'SUGGESTED',
    suggestedApplicationId: 'app-mez3-5',
  },
];
